import { Component, Inject, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { forkJoin } from 'rxjs';
import { CompanyBoardService } from '../../../services/companies/company-board.service';
import { LutService } from '../../../services/lut.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { BoardAllPeriodsMemberDto, BoardAllPeriodsMemberUpsertDto, BoardAllPeriodsUpsertRequest } from '../../../models/api/companyBoardMemberDto';
import { CeoTurnoverReason } from '../../../models/ceoTurnoverReason';
import { BoardMemberOccupation } from '../../../models/boardMemberOccupation';
import { Helpers } from '../../../utilities/helpers';

export interface FixDirectorsDialogData {
  companyId: string;
}

@Component({
  selector: 'app-fix-directors-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    ReactiveFormsModule,
    RouterLink,
    DragDropModule,
    NgxSpinnerModule
  ],
  templateUrl: './fix-directors-dialog.component.html',
  styleUrls: ['./fix-directors-dialog.component.scss']
})
export class FixDirectorsDialogComponent implements OnInit {
  private boardService = inject(CompanyBoardService);
  private lutService = inject(LutService);
  private spinnerService = inject(NgxSpinnerService);
  private toast = inject(HotToastService);
  private fb = inject(FormBuilder);
  private confirmationDialog = inject(ConfirmationDialogService);

  rowsData: BoardAllPeriodsMemberDto[] = [];
  dataSource = new MatTableDataSource<BoardAllPeriodsMemberDto>([]);
  displayedColumns: string[] = ['index', 'name', 'type', 'occupation', 'years', 'ceoTurnover', 'ceoHireDate', 'ceoEndDate', 'hireDate', 'endDate'];
  ceoTurnoverReasons: CeoTurnoverReason[] = [];
  private occupationsById = new Map<string, string>();

  form: FormGroup = this.fb.group({
    rows: this.fb.array([])
  });

  constructor(
    public dialogRef: MatDialogRef<FixDirectorsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FixDirectorsDialogData
  ) {}

  get rows(): FormArray {
    return this.form.get('rows') as FormArray;
  }

  get hasChanges(): boolean {
    return this.form.dirty;
  }

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.spinnerService.show();
    forkJoin({
      periods: this.boardService.getAllPeriods(this.data.companyId),
      reasons: this.lutService.getCeoTurnoverReasons(),
      occupations: this.lutService.getBoardMemberOccupations()
    }).subscribe({
      next: ({ periods, reasons, occupations }) => {
        this.ceoTurnoverReasons = [...reasons].sort((a, b) => a.name.localeCompare(b.name));
        this.occupationsById = new Map(occupations.map((o: BoardMemberOccupation) => [o.id, o.name]));
        const board = periods.members
          .filter(m => !m.isExecutive)
          .sort((a, b) => a.directorFullName.localeCompare(b.directorFullName));
        const executive = periods.members
          .filter(m => m.isExecutive)
          .sort((a, b) => a.directorFullName.localeCompare(b.directorFullName));
        this.rowsData = [...board, ...executive];
        this.buildRows(this.rowsData);
        this.dataSource.data = this.rowsData;
        this.spinnerService.hide();
      },
      error: () => {
        this.spinnerService.hide();
        this.toast.error('Failed to load director periods');
      }
    });
  }

  private buildRows(members: BoardAllPeriodsMemberDto[]): void {
    const array = this.fb.array(members.map(m => this.createRow(m)));
    this.form.setControl('rows', array);
  }

  private createRow(member: BoardAllPeriodsMemberDto): FormGroup {
    return this.fb.group({
      hireDate: [Helpers.toDisplayDate(member.hireDate)],
      endDate: [Helpers.toDisplayDate(member.endDate)],
      ceoHireDate: [Helpers.toDisplayDate(member.ceoHireDate)],
      ceoEndDate: [Helpers.toDisplayDate(member.ceoEndDate)],
      ceoTurnoverReasonId: [member.ceoTurnoverReasonId || '']
    });
  }

  occupationName(id?: string): string {
    return id ? this.occupationsById.get(id) ?? '' : '';
  }

  toggleRowHighlight(row: BoardAllPeriodsMemberDto & { highlighted?: boolean }): void {
    row.highlighted = !row.highlighted;
  }

  onDateFocus(event: Event): void {
    Helpers.onDateInputFocus(event);
  }

  onDateBlur(event: Event): void {
    Helpers.onDateInputBlur(event);
  }

  onDateInput(event: Event): void {
    Helpers.onDateInputFormat(event);
  }

  onSave(): void {
    if (!this.hasChanges) return;

    const members: BoardAllPeriodsMemberUpsertDto[] = [];
    this.rows.controls.forEach((group, i) => {
      if (!group.dirty) return;
      const source = this.rowsData[i];
      const val = group.value;
      members.push({
        memberId: source.memberId,
        isExecutive: source.isExecutive,
        hireDate: Helpers.toIsoDate(val.hireDate) || undefined,
        endDate: Helpers.toIsoDate(val.endDate) || undefined,
        ceoHireDate: Helpers.toIsoDate(val.ceoHireDate) || undefined,
        ceoEndDate: Helpers.toIsoDate(val.ceoEndDate) || undefined,
        ceoTurnoverReasonId: val.ceoTurnoverReasonId || undefined
      });
    });

    if (members.length === 0) return;

    this.confirmationDialog
      .openSaveConfirmationDialog(`Save changes to ${members.length} director ${members.length === 1 ? 'period' : 'periods'}?`)
      .subscribe(confirmed => {
        if (confirmed) {
          this.persistChanges({ members });
        }
      });
  }

  private persistChanges(request: BoardAllPeriodsUpsertRequest): void {
    this.spinnerService.show();
    this.boardService.updateAllPeriods(this.data.companyId, request).subscribe({
      next: response => {
        this.spinnerService.hide();
        this.toast.success('Saved');
        this.applyResponse(response.members);
      },
      error: () => {
        this.spinnerService.hide();
        this.toast.error('Failed to save director periods');
      }
    });
  }

  private applyResponse(members: BoardAllPeriodsMemberDto[]): void {
    const board = members
      .filter(m => !m.isExecutive)
      .sort((a, b) => a.directorFullName.localeCompare(b.directorFullName));
    const executive = members
      .filter(m => m.isExecutive)
      .sort((a, b) => a.directorFullName.localeCompare(b.directorFullName));
    this.rowsData = [...board, ...executive];
    this.buildRows(this.rowsData);
    this.dataSource.data = this.rowsData;
    this.form.markAsPristine();
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
